import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import { TABLE_HEADER_ROW_SX } from '../../../../utils/typographyStyles';
import HeaderProfile from '../../../../components/header_Profile/header_Profile.index';
import InvoicePagination from '../../../../components/Billing/Invoice/InvoicePagination';
import InvoiceSearchBar from '../../../../components/Billing/Invoice/InvoiceSearchBar';
import TableLoader from '../../../../components/common/TableLoader';
import NoDataRow from '../../../../components/common/NoDataRow';
import StatusChip from '../../../../components/Billing/Invoice/InvoiceStatusChip';
import RemarkPopover from '../../../../components/Billing/common/RemarkPopover';
import { usePharmacyInvoiceLogsQuery, PharmacyInvoiceLogItem } from './pharmacyInvoiceLogHelper';
import { useClinic } from '../../../../components/ClinicProvider';
import { formatDateDMY } from '../../../../utils/dateUtils';
import useCurrency from '../../../../components/CurrencyProvider';
// import { ReactComponent as PrinterIcon } from '../../../../assets/SVG/printerblue.svg';
// import { ReactComponent as ViewIcon } from '../../../../assets/SVG/eye_view.svg';

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 15, 25, 50];

const LABELS = {
  title: 'Invoice Logs',
  searchPlaceholder: 'Search By Invoice No.',
  noData: 'No pharmacy invoice logs found.',
  loading: 'Loading...',
};

const ACTION_LABELS = {
  view: 'View Invoice',
  print: 'Print Invoice',
  openRemark: 'Show remark',
};

/** Table header cell config */
const getHeaderCells = (currencySymbol: string): Array<{
  key: string;
  label: string;
  minWidth?: number;
  align?: 'center' | 'left' | 'right';
  noWrap?: boolean;
}> => [
  { key: 'date', label: 'Date', minWidth: 120, noWrap: true },
  { key: 'billNo', label: 'Bill No.', noWrap: true },
  { key: 'invoiceNo', label: 'Invoice No.', noWrap: true },
  { key: 'referredDoctor', label: 'Referred Doctor', noWrap: true },
  { key: 'noOfItem', label: 'No. of Items', align: 'center', noWrap: true },
  { key: 'totalAmt', label: `Total Amt (${currencySymbol})`, align: 'right', noWrap: true },
  { key: 'concessionAmt', label: `Concession Amt (${currencySymbol})`, align: 'right', noWrap: true },
  { key: 'netAmt', label: `Net Amt (${currencySymbol})`, align: 'right', noWrap: true },
  { key: 'paidAmt', label: `Paid Amt (${currencySymbol})`, align: 'right', noWrap: true },
  { key: 'balanceAmt', label: `Balance Amt (${currencySymbol})`, align: 'right', noWrap: true },
  { key: 'billRemark', label: 'Bill Remark', noWrap: true },
  { key: 'status', label: 'Status', noWrap: true },
//   { key: 'actions', label: 'Actions', align: 'center', noWrap: true },
];

const commonCellSx = {
  verticalAlign: 'middle' as const,
  px: 2.5,
  py: 1.5,
};

// ============================================================================
// Helper: Normalize status
// ============================================================================

const normalizePaymentStatus = (status: string): string => {
  const s = (status || '').toLowerCase().replace(/_/g, ' ');
  switch (s) {
  case 'paid':
    return 'Paid';
  case 'unpaid':
    return 'Unpaid';
  case 'partially paid':
  case 'partial':
    return 'Partially Paid';
  default:
    return status || 'Unpaid';
  }
};

// ============================================================================
// Types
// ============================================================================

interface PharmacyInvoiceRow {
  id: number;
  date: string;
  billNo: string;
  invoiceNo: string;
  referredDoctor: string;
  noOfItem: number;
  totalAmt: string;
  concessionAmt: string;
  netAmt: string;
  paidAmt: string;
  balanceAmt: string;
  status: string;
  billRemark: string | null;
}

// ============================================================================
// Component
// ============================================================================

const PharmacyInvoiceLogs: React.FC = () => {
  const navigate = useNavigate();
  const { id: patientIdFromUrl } = useParams<{ id: string }>();
  const { ClinicId } = useClinic();
  const { currencySymbol } = useCurrency();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Generate header cells with currency symbol
  const headerCells = useMemo(() => getHeaderCells(currencySymbol), [currencySymbol]);

  // Remark popover state
  const [remarkAnchorEl, setRemarkAnchorEl] = useState<null | HTMLElement>(null);
  const [remarkText, setRemarkText] = useState<string>('');

  // Fetch pharmacy invoice logs for this patient
  const { data, isLoading, isFetching } = usePharmacyInvoiceLogsQuery({
    patientId: patientIdFromUrl ? Number(patientIdFromUrl) : null,
    page,
    limit: pageSize,
    search,
    enabled: !!ClinicId && !!patientIdFromUrl,
  });

  const totalCount = data?.data?.meta?.total_count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Map API data to table rows
  const tableRows = useMemo<PharmacyInvoiceRow[]>(() => {
    if (!data?.data?.invoices?.length) return [];
    return data.data.invoices.map((invoice: PharmacyInvoiceLogItem) => ({
      id: invoice.id,
      date: invoice.date_of_invoice || invoice.date || '-',
      billNo: invoice.bill_no || '-',
      invoiceNo: invoice.invoice_no || '-',
      referredDoctor: invoice.referred_doctor || '-',
      noOfItem: invoice.no_of_item || 0,
      totalAmt: invoice.total_amt || '0.00',
      concessionAmt: invoice.concession_amt || '0.00',
      netAmt: invoice.net_amt || invoice.net_amount || '0.00',
      paidAmt: invoice.paid_amt || '0.00',
      balanceAmt: invoice.balance_amt || '0.00',
      status: normalizePaymentStatus(invoice.payment_status || invoice.status || ''),
      billRemark: invoice.bill_remark,
    }));
  }, [data]);

  // Handlers
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Remark popover handlers
  const handleOpenRemark = (event: React.MouseEvent<HTMLElement>, text: string) => {
    setRemarkAnchorEl(event.currentTarget);
    setRemarkText(text);
  };

  const handleCloseRemark = () => {
    setRemarkAnchorEl(null);
    setRemarkText('');
  };

  // View invoice handler
  //   const handleViewInvoice = (invoiceId: number, patientId: number) => {
  //     navigate(`/admin/billing/pharmacy/view/${patientId}`, {
  //       state: { invoice_id: invoiceId },
  //     });
  //   };

  //   // Print invoice handler
  //   const handlePrintInvoice = (invoiceId: number) => {
  //     // TODO: Implement pharmacy invoice print
  //     // eslint-disable-next-line no-console
  //     console.log('Print pharmacy invoice:', invoiceId);
  //   };

  if (isLoading) {
    return <TableLoader minHeight={320} message={LABELS.loading} size={42} thickness={3} />;
  }

  return (
    <>
      <HeaderProfile />
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mx: 2, my: 2 }}>
          <IconButton
            size="small"
            onClick={handleGoBack}
            sx={{ mr: 1.5, borderRadius: 2, border: '1px solid #E5E7EB' }}
            aria-label="Go back"
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
          </IconButton>

          <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1, color: '#111827' }}>
            {LABELS.title}
          </Typography>

          <InvoiceSearchBar
            value={search}
            onChange={handleSearchChange}
            placeholder={LABELS.searchPlaceholder}
          />
        </Box>


        {/* Table */}
        <Box sx={{ mx: 2, position: 'relative' }}>
          {isFetching && !isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                bgcolor: 'primary.main',
                zIndex: 1,
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                },
              }}
            />
          )}
          <TableContainer
            component={Paper}
            sx={{
              minWidth: 360,
              borderRadius: 3,
              boxShadow: '0px 1px 2px rgba(15, 23, 42, 0.06)',
            }}
          >
            <Table
              size="small"
              aria-label="pharmacy invoice logs table"
              sx={{
                borderCollapse: 'separate',
                borderSpacing: 0,
                '& th, & td': {
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
              }}
            >
              <TableHead>
                <TableRow sx={TABLE_HEADER_ROW_SX}>
                  {headerCells.map((h, index) => (
                    <TableCell
                      key={h.key}
                      align={h.align}
                      sx={{
                        ...(h.minWidth ? { minWidth: h.minWidth } : {}),
                        ...(h.noWrap ? { whiteSpace: 'nowrap' } : {}),
                        px: 2.5,
                        ...(index === 0 ? { borderTopLeftRadius: 12, borderBottomLeftRadius: 12 } : {}),
                        ...(index === headerCells.length - 1 ? { borderTopRightRadius: 12, borderBottomRightRadius: 12 } : {}),
                      }}
                    >
                      {h.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.length > 0 ? (
                  tableRows.map((row, idx) => {
                    return (
                      <TableRow
                        key={row.id ?? idx}
                        hover
                        sx={{
                          height: 60,
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                        }}
                      >
                        <TableCell sx={{ ...commonCellSx, minWidth: 120, whiteSpace: 'nowrap' }}>
                          {row.date ? formatDateDMY(row.date) : '-'}
                        </TableCell>
                        <TableCell sx={commonCellSx}>{row.billNo || '-'}</TableCell>
                        <TableCell sx={commonCellSx}>{row.invoiceNo || '-'}</TableCell>
                        <TableCell sx={{ ...commonCellSx, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>
                          {row.referredDoctor || '-'}
                        </TableCell>
                        <TableCell align="center" sx={commonCellSx}>{row.noOfItem}</TableCell>
                        <TableCell align="right" sx={commonCellSx}>{row.totalAmt || '-'}</TableCell>
                        <TableCell align="right" sx={commonCellSx}>{row.concessionAmt || '-'}</TableCell>
                        <TableCell align="right" sx={commonCellSx}>{row.netAmt || '-'}</TableCell>
                        <TableCell align="right" sx={commonCellSx}>{row.paidAmt || '-'}</TableCell>
                        <TableCell align="right" sx={commonCellSx}>{row.balanceAmt || '-'}</TableCell>
                        <TableCell sx={commonCellSx}>
                          {row.billRemark && row.billRemark.trim().length > 0 && row.billRemark.trim() !== '-' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: 260 }}>
                              <Tooltip title={ACTION_LABELS.openRemark} arrow>
                                <IconButton
                                  size="small"
                                  sx={{ p: 0, color: '#5A8AEA', flexShrink: 0 }}
                                  aria-label={ACTION_LABELS.openRemark}
                                  tabIndex={0}
                                  onClick={(e) => handleOpenRemark(e, row.billRemark || '')}
                                >
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>
                                {row.billRemark}
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', width: '30%' }}>-</Box>
                          )}
                        </TableCell>
                        <TableCell sx={commonCellSx}>
                          <StatusChip status={row.status} width={110} height={24} sx={{ fontSize: 12, mt: 0.5 }} />
                        </TableCell>
                        
                        {/* 
                            Temp commented want to confirm with mangesh Sir 
                        */}
                        
                        {/* <TableCell align="center" sx={commonCellSx}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, minWidth: 80 }}>
                            <Tooltip title={ACTION_LABELS.view} arrow>
                              <IconButton
                                size="small"
                                aria-label={ACTION_LABELS.view}
                                tabIndex={0}
                                onClick={() => handleViewInvoice(row.id, Number(patientIdFromUrl))}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={ACTION_LABELS.print} arrow>
                              <IconButton
                                size="small"
                                aria-label={ACTION_LABELS.print}
                                tabIndex={0}
                                onClick={() => handlePrintInvoice(row.id)}
                              >
                                <PrinterIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell> */}
                      </TableRow>
                    );
                  })
                ) : (
                  <NoDataRow message={LABELS.noData} colSpan={headerCells.length} />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Pagination */}
        <Box sx={{ mx: 2, mt: 2.5 }}>
          <InvoicePagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalEntries={totalCount}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={handlePageSizeChange}
          />
        </Box>

        {/* Remark popover */}
        <RemarkPopover
          anchorEl={remarkAnchorEl}
          open={Boolean(remarkAnchorEl)}
          content={remarkText}
          onClose={handleCloseRemark}
        />
      </Box>
    </>
  );
};

export default PharmacyInvoiceLogs;
